import {
  Button,
  Image,
  Tag,
  font,
  media,
} from "@literati/public-ui-shared-react";
import styled from "styled-components";
import {
  SwipeStatusAction,
  useSwipe,
  SwipeData,
} from "components/bookShuffle/useSwipe";
import { PreferenceValue } from "lib/api/onboarding";
import { colors } from "@literati/public-ui-shared-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { bookShuffleDeckCardSizeStyles } from "components/bookShuffle/bookShuffleStyles";
import { SwipeStatus } from "components/bookShuffle/bookShuffleTypes";
import { BookShuffleDecisionIcon } from "components/bookShuffle/BookShuffleDecisionIcon";
import BookShuffleDialogBookDetails, {
  shouldShowDetailsSectionElements,
} from "components/bookShuffle/BookShuffleDialogBookDetails";
import { formatAuthorNames } from "components/bookShuffle/utils";
import { titleCase } from "utils/string";
import {
  trackBookShuffleCardDecision,
  trackBookShuffleEvent,
} from "analytics/bookShuffleAnalytics";
import useBookShuffle from "components/bookShuffle/useBookShuffle";
import useCardStyles from "components/bookShuffle/useCardStyles";

const CardDiv = styled.div<{ $swiping: boolean; $rows: number }>`
  ${bookShuffleDeckCardSizeStyles}
  border-radius: 1rem;
  background-color: white;
  cursor: ${({ $swiping }) => ($swiping ? "grabbing" : "grab")};
  touch-action: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem 1rem 1rem;
`;

const BookImage = styled(Image)`
  position: relative;
  width: 7rem;
  height: 7rem;
  min-height: 7rem;
  margin-bottom: 0.75rem;

  &,
  & * {
    user-drag: none;
    -webkit-user-drag: none;
    user-select: none;
    -moz-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
  }

  ${media.lg} {
    width: 9.5rem;
    height: 9.5rem;
    min-height: 9.5rem;
    margin-bottom: 1.25rem;
  }
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
  /* limit to 3 lines of Tags -- mixing px in here accounts for 
  growth in text size/flex gap at larger font settings */
  max-height: calc(3.25rem + 48px);
  overflow: hidden;

  margin: 0.5rem 1rem 0.5rem;
  ${media.lg} {
    margin: 0.5rem 1rem 0.75rem;
  }
`;

const Title = styled.cite`
  display: block;
  text-align: center;
  font-weight: ${font.weight.bold};
  font-size: 0.9375rem;
  margin: 0 1rem 0.25rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  font-style: normal;
  user-select: none;
`;

const Authors = styled.p`
  font-weight: ${font.weight.roman};
  font-size: 0.8125rem;
  text-align: center;
  color: ${colors.warmGray3};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  user-select: none;
`;

const Description = styled.p<{ $expanded?: boolean }>`
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: center;
  color: ${colors.warmGray3};
  font-size: 0.875rem;
  height: 3.94rem;
  user-select: none;

  margin: 0 1rem 0.25rem;
  ${media.lg} {
    margin: 0 1rem 0.5rem;
  }
`;

/**
 * Allows ViewDetailsButton to flow into the white
 * space of the button container below it
 */
const ViewDetailsButtonContainer = styled.div`
  position: relative;
  width: 100%;
  height: 0;
`;

const ViewDetailsButton = styled(Button)`
  font-size: 0.875rem;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
`;

const ButtonContainer = styled.div`
  width: 100%;
  flex: 2;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
`;

const DecisionButton = styled(Button)`
  border-radius: 50%;
  background-color: lightblue;
  width: 64px;
  height: 64px;
`;

const COVER_IMG_PLACEHOLDER =
  "https://ctfassets.literati.com/vqtz4cq2w5ts/28qbG2kRvefzvWF5y5rUCN/fead1dfa0c8f2a0d4c2699bbe8a6cc23/Universal.png";

const VIEW_DETAILS_BUTTON_LABEL = "View Details";

export type BookShuffleCardProps = {
  className?: string;
  bookIndex: number;
};

export const BookShuffleCard = (props: BookShuffleCardProps) => {
  const { className, bookIndex } = props;

  const styles = useCardStyles(bookIndex);

  const { currentIndex, books, setDecision, completeAnimation, setSwipeData, distanceSwiped } =
    useBookShuffle();

  const {
    contributors,
    cover,
    description,
    externalCoverUrl,
    summaryTags,
    title,
  } = useMemo(() => books[bookIndex], [books, bookIndex]);

  const handleAnimateCycleComplete = useCallback(
    (swipeData: SwipeData) => {
      if (bookIndex !== currentIndex) return;
      setSwipeData({
        swipeData,
      });
    },
    [setSwipeData, bookIndex, currentIndex]
  );

  const onStatusDecision = useCallback(
    async (swipeStatus: SwipeStatus, swipeStatusAction: SwipeStatusAction) => {
      trackBookShuffleCardDecision(swipeStatus, swipeStatusAction);

      const preferenceValue =
        swipeStatus == SwipeStatus.REJECTED
          ? PreferenceValue.Dislike
          : PreferenceValue.Like;

      setDecision({ preferenceValue });
    },
    [setDecision]
  );

  const { accept, reject, ref, swiping, reset } = useSwipe<HTMLDivElement>({
    onStatusDecision,
    onExitComplete: completeAnimation,
    onAnimateCycleComplete: handleAnimateCycleComplete,
  });

  useEffect(() => {
    if (bookIndex === currentIndex) reset();
  }, [currentIndex, bookIndex, reset]);

  const formattedAuthorNames = formatAuthorNames(contributors);

  const { showDetailsSection: shouldShowBookDetailsDialog } =
    shouldShowDetailsSectionElements(contributors, description);

  const [isOpenBookDetailsDialog, setIsOpenBookDetailsDialog] = useState(false);
  
  
  const openBookDetailsDialog = useCallback(() => {
    // prevent opening when missing a book data
    if (!shouldShowBookDetailsDialog) return;
    // prevent opening when swiping
    if (distanceSwiped > 1) return;
    setIsOpenBookDetailsDialog(true)
    trackBookShuffleEvent({
      eventName: "Clicked",
      label: VIEW_DETAILS_BUTTON_LABEL,
    });
  }, [shouldShowBookDetailsDialog, distanceSwiped]);

  if (!styles) return null;

  return (
    <CardDiv
      style={styles.cardStyle}
      className={className}
      $swiping={swiping}
      ref={ref}
      $rows={description ? 5 : 4}
    >
      <BookImage
        src={externalCoverUrl ?? cover ?? COVER_IMG_PLACEHOLDER}
        contain
        layout="fill"
        objectFit="contain"
        onClick={openBookDetailsDialog}
      />
      <div>
        <Title>{title}</Title>
        {formattedAuthorNames ? (
          <Authors>by {formattedAuthorNames}</Authors>
        ) : null}
      </div>
      {summaryTags?.length > 2 ? (
        <Tags>
          {summaryTags.map((tag, i) => (
            <Tag key={`${tag}-${i}`} color="frenchBlueShade2">
              {titleCase(tag)}
            </Tag>
          ))}
        </Tags>
      ) : description ? (
        <Description
          // In practice, this should only contain html entities
          dangerouslySetInnerHTML={{ __html: description }}
        ></Description>
      ) : null}
      {shouldShowBookDetailsDialog && (
        <ViewDetailsButtonContainer>
          <ViewDetailsButton
            variant="link"
            onClick={openBookDetailsDialog}
            >
            {VIEW_DETAILS_BUTTON_LABEL}
          </ViewDetailsButton>
        </ViewDetailsButtonContainer>
      )}
      <BookShuffleDialogBookDetails
          contributors={contributors}
          externalCoverUrl={externalCoverUrl}
          title={title}
          description={description}
          dialogOptions={{
            rootProps:{
              open: isOpenBookDetailsDialog,
              onOpenChange: setIsOpenBookDetailsDialog,
            }
          }}
      />
      <ButtonContainer>
        {/* analytics click handlers are implemented in onStatusDecision */}
        <DecisionButton
          onClick={reject}
          variant="unstyled"
          size="none"
          aria-label="dislike"
        >
          <BookShuffleDecisionIcon type={SwipeStatus.REJECTED} />
        </DecisionButton>
        {/* analytics click handlers are implemented in onStatusDecision */}
        <DecisionButton
          onClick={accept}
          variant="unstyled"
          size="none"
          aria-label="like"
        >
          <BookShuffleDecisionIcon type={SwipeStatus.ACCEPTED} />
        </DecisionButton>
      </ButtonContainer>
    </CardDiv>
  );
};
